import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, AlertDescription } from '@talentmesh/ui/components/alert';
import { Button } from '@talentmesh/ui/components/button';
import { Card, CardContent } from '@talentmesh/ui/components/card';
import { Field, FieldError } from '@talentmesh/ui/components/field';
import { RHFDefaultInput } from '@talentmesh/ui/components/rhf-default-input';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { TypographyH2, TypographyP } from '@talentmesh/ui/components/typography';
import { cn } from '@talentmesh/ui/lib/utils';
import { TriangleAlertIcon } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import OneClickLoadingAnimation from './OneClickLoadingAnimation';
import AiSparklesIcon from '@/Components/TalentMeshIcons/AiSparklesIcon';
import UIStrings from '@/Utils/UIStrings';

interface OneClickRecruitmentFormProps {
    onSubmit: (url: string) => void;
    isSubmitting: boolean;
    onSkip: () => void;
    /** Inline error shown on a hard failure (e.g. unreachable URL); the recruiter can retry or skip. */
    errorMessage?: string;
}

// Scraping linkedin.com does not work, so a LinkedIn URL is rejected with an actionable message.
const isLinkedInUrl = (value: string): boolean => {
    try {
        const host = new URL(value).hostname.toLowerCase();
        return host === 'linkedin.com' || host.endsWith('.linkedin.com');
    } catch {
        return false;
    }
};

const oneClickSchema = z.object({
    url: z
        .string()
        .trim()
        .min(1, UIStrings.Required)
        .pipe(z.url({ error: UIStrings.JobAdUrlInvalid }))
        .refine((value) => !isLinkedInUrl(value), UIStrings.JobAdUrlLinkedInNotSupported),
});

type OneClickFormValues = z.infer<typeof oneClickSchema>;

const OneClickRecruitmentForm = ({ onSubmit, onSkip, isSubmitting, errorMessage }: OneClickRecruitmentFormProps) => {
    const form = useForm<OneClickFormValues>({
        resolver: zodResolver(oneClickSchema),
        mode: 'all',
        defaultValues: { url: '' },
    });

    // input fields is implemented in a custom way in order to be albe to render error which
    // should cover the whole width
    const urlFieldName = 'url';

    const { handleSubmit, formState } = form;
    const error = formState.errors[urlFieldName];

    const handleValidSubmit = ({ url }: OneClickFormValues) => {
        onSubmit(url.trim());
    };

    // While the (25–30s) backend pipeline runs, replace the entry card with a paced progress
    // animation so the recruiter sees meaningful, moving feedback instead of a frozen form.
    if (isSubmitting) {
        return <OneClickLoadingAnimation />;
    }

    return (
        <div className="flex h-full items-center justify-center" data-cy="one-click-entry">
            <Card className="w-full max-w-xl">
                <CardContent className="flex flex-col gap-6 p-8">
                    <div className="flex flex-col gap-2">
                        <TypographyH2>{UIStrings.OneClickTitle}</TypographyH2>
                        <TypographyP>{UIStrings.OneClickDescription}</TypographyP>
                    </div>

                    <FormProvider {...form}>
                        <form className="flex flex-col gap-3" onSubmit={handleSubmit(handleValidSubmit)} noValidate>
                            <div className="flex w-full flex-row gap-4">
                                <Field
                                    className={cn('w-full')}
                                    data-invalid={error ? true : undefined}
                                    data-cy="one-click-url-input"
                                >
                                    <RHFDefaultInput
                                        name={urlFieldName}
                                        type={urlFieldName}
                                        aria-label={UIStrings.JobAdUrlLabel}
                                        placeholder={UIStrings.JobAdUrlPlaceholder}
                                        disabled={isSubmitting}
                                    />
                                </Field>

                                <Button type="submit" disabled={isSubmitting} data-cy="one-click-submit">
                                    {isSubmitting ? <Spinner aria-hidden /> : <AiSparklesIcon />}
                                    {UIStrings.CreateRecruitment}
                                </Button>
                            </div>

                            <FieldError>{error?.message ? String(error.message) : null}</FieldError>
                        </form>
                    </FormProvider>

                    {errorMessage && (
                        <Alert variant="destructive" data-cy="one-click-error">
                            <TriangleAlertIcon />
                            <AlertDescription>{errorMessage}</AlertDescription>
                        </Alert>
                    )}

                    <span className="bg-border h-px grow" />

                    <div className="flex justify-center">
                        <Button
                            type="button"
                            variant="link"
                            onClick={onSkip}
                            disabled={isSubmitting}
                            data-cy="one-click-skip"
                        >
                            {UIStrings.OrGoToManualCreation}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default OneClickRecruitmentForm;
